import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Linkedin, Building2, Briefcase, RefreshCw } from 'lucide-react';
import Header from '../components/layout/Header';
import ContactTimeline from '../components/ContactTimeline';
import api from '../services/api';

interface ContactDetail {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  title?: string;
  company?: string;
  status: string;
  relationship_warmth?: number;
  warmth_factors?: Record<string, any>;
  linkedin_url?: string;
  last_email_interaction?: string;
  last_linkedin_interaction?: string;
  last_meeting_interaction?: string;
  email_engagement_score?: number;
}

interface EmailStats {
  total: number;
  sent: number;
  received: number;
  avg_their_response_hours?: number;
  avg_our_response_hours?: number;
  emails_per_month?: number;
  first_email?: string;
  last_email?: string;
}

function WarmthBar({ score, label }: { score: number; label: string }) {
  const color = score >= 50 ? 'var(--success)' : score >= 20 ? 'var(--warning)' : 'var(--error)';
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-20 h-1.5 rounded-full" style={{ background: 'var(--bg-input)' }}>
          <div className="h-full rounded-full" style={{ width: `${Math.min(score, 100)}%`, background: color }} />
        </div>
        <span style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [emailStats, setEmailStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadContact();
    loadEmailStats();
  }, [id]);

  async function loadContact() {
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${id}`);
      setContact(data);
    } catch (err) {
      console.error('Failed to load contact:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadEmailStats() {
    if (!id) return;
    try {
      const { data } = await api.get(`/gmail/stats/${id}`);
      setEmailStats(data);
    } catch {
      // Email stats may not exist yet
    }
  }

  async function syncEmails() {
    if (!id) return;
    setSyncing(true);
    try {
      await api.post(`/gmail/sync/${id}`);
      await loadEmailStats();
    } catch (err) {
      console.error('Gmail sync failed:', err);
    } finally {
      setSyncing(false);
    }
  }

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Contact" subtitle="Loading..." />
        <div className="p-8">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading contact...</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="page-enter">
        <Header title="Contact" subtitle="Not found" />
        <div className="p-8">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Contact not found.</p>
          <button onClick={() => navigate('/contacts')} className="mt-4 text-sm underline" style={{ color: 'var(--netrun-green)' }}>
            Back to contacts
          </button>
        </div>
      </div>
    );
  }

  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown';
  const warmth = contact.relationship_warmth || 0;
  const factors = contact.warmth_factors || {};

  return (
    <div className="page-enter">
      <Header title={fullName} subtitle={contact.title || contact.company || ''} />

      <div className="p-4 md:p-8">
        {/* Client Prep CTA */}
        <button
          onClick={() => navigate(`/clients/${id}/prep`)}
          className="mb-4 px-3 py-2 rounded text-sm font-medium"
          style={{ background: 'var(--netrun-green)', color: 'white', border: 'none' }}
        >
          Open Client Prep →
        </button>
        {/* Back button */}
        <button
          onClick={() => navigate('/contacts')}
          className="flex items-center gap-2 mb-6 text-sm cursor-pointer"
          style={{ color: 'var(--text-muted)', background: 'none', border: 'none' }}
        >
          <ArrowLeft size={16} />
          Back to contacts
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Contact info + warmth */}
          <div className="space-y-4">
            {/* Contact card */}
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)' }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center font-display text-lg"
                  style={{ background: 'var(--bg-hover)', color: 'var(--netrun-green)' }}>
                  {(contact.first_name?.[0] || '?')}{(contact.last_name?.[0] || '')}
                </div>
                <div>
                  <p className="text-base font-body" style={{ color: 'var(--text-primary)' }}>{fullName}</p>
                  {contact.title && (
                    <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <Briefcase size={12} /> {contact.title}
                    </p>
                  )}
                  {contact.company && (
                    <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <Building2 size={12} /> {contact.company}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {contact.email && (
                  <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <Mail size={14} />
                    <span>{contact.email}</span>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <Phone size={14} />
                    <span>{contact.phone}</span>
                  </div>
                )}
                {contact.linkedin_url && (
                  <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <Linkedin size={14} />
                    <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="underline" style={{ color: 'var(--netrun-green)' }}>
                      LinkedIn Profile
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Warmth breakdown */}
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)' }}>
              <h3 className="text-sm font-body mb-3" style={{ color: 'var(--text-primary)' }}>
                Relationship Warmth
              </h3>
              <div className="mb-3">
                <WarmthBar score={warmth} label="Overall" />
              </div>
              <div className="space-y-2">
                {factors.linkedin_warmth != null && (
                  <WarmthBar score={factors.linkedin_warmth} label="LinkedIn" />
                )}
                {factors.email_warmth != null && (
                  <WarmthBar score={factors.email_warmth} label="Email" />
                )}
                {factors.calendar_warmth != null && (
                  <WarmthBar score={factors.calendar_warmth} label="Meetings" />
                )}
              </div>
            </div>

            {/* Email engagement stats */}
            {emailStats && emailStats.total > 0 && (
              <div className="rounded-xl p-5" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)' }}>
                <h3 className="text-sm font-body mb-3" style={{ color: 'var(--text-primary)' }}>
                  Email Engagement
                </h3>
                <div className="space-y-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <div className="flex justify-between">
                    <span>Total emails</span>
                    <span style={{ color: 'var(--text-primary)' }}>{emailStats.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sent / Received</span>
                    <span style={{ color: 'var(--text-primary)' }}>{emailStats.sent} / {emailStats.received}</span>
                  </div>
                  {emailStats.emails_per_month != null && (
                    <div className="flex justify-between">
                      <span>Emails/month</span>
                      <span style={{ color: 'var(--text-primary)' }}>{emailStats.emails_per_month}</span>
                    </div>
                  )}
                  {emailStats.avg_their_response_hours != null && (
                    <div className="flex justify-between">
                      <span>Their avg response</span>
                      <span style={{ color: 'var(--text-primary)' }}>{emailStats.avg_their_response_hours}h</span>
                    </div>
                  )}
                  {emailStats.avg_our_response_hours != null && (
                    <div className="flex justify-between">
                      <span>Your avg response</span>
                      <span style={{ color: 'var(--text-primary)' }}>{emailStats.avg_our_response_hours}h</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sync button */}
            {contact.email && (
              <button
                onClick={syncEmails}
                disabled={syncing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-body cursor-pointer transition-all"
                style={{
                  background: syncing ? 'var(--bg-input)' : 'var(--netrun-green)',
                  border: 'none',
                  color: syncing ? 'var(--text-muted)' : 'var(--netrun-black)',
                  opacity: syncing ? 0.6 : 1,
                }}
              >
                <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Gmail'}
              </button>
            )}
          </div>

          {/* Right: Timeline */}
          <div className="lg:col-span-2">
            <div className="rounded-xl p-5" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)' }}>
              <h3 className="text-sm font-body mb-4" style={{ color: 'var(--text-primary)' }}>
                Interaction Timeline
              </h3>
              <ContactTimeline contactId={id!} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
